import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { 
  Users, 
  UserPlus, 
  Mail, 
  Clock,
  Trash2,
  Crown,
  Shield,
  UserCog,
  UserCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleIcons = {
  owner: Crown,
  admin: Shield,
  sales_manager: UserCog,
  sales_rep: UserCircle
};

const roleColors = {
  owner: "bg-amber-100 text-amber-700",
  admin: "bg-purple-100 text-purple-700",
  sales_manager: "bg-blue-100 text-blue-700",
  sales_rep: "bg-slate-100 text-slate-600"
};

export default function TeamPage({ user }) {
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("sales_rep");
  const [inviting, setInviting] = useState(false);
  const [org, setOrg] = useState(null);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      // Fetch team members
      const membersResponse = await fetch(`${API}/users`, { credentials: "include" });
      if (membersResponse.ok) {
        const data = await membersResponse.json();
        setMembers(data);
      }

      // Fetch pending invitations (only for owners/admins)
      const invitesResponse = await fetch(`${API}/organization/invitations`, { 
        credentials: "include" 
      });
      if (invitesResponse.ok) {
        const data = await invitesResponse.json();
        setInvitations(data.invitations || []);
      }

      // Fetch organization info
      const meResponse = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (meResponse.ok) {
        const data = await meResponse.json();
        setOrg(data.organization);
      }
    } catch (error) {
      console.error("Failed to fetch team data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setInviting(true);
    try {
      const response = await fetch(`${API}/organization/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Invitation sent!");
        setInviteDialogOpen(false);
        setInviteEmail("");
        setInviteRole("sales_rep");
        fetchTeamData();
      } else {
        toast.error(data.detail || "Failed to send invitation");
      }
    } catch (error) {
      toast.error("Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvitation = async (email) => {
    if (!window.confirm(`Cancel invitation for ${email}?`)) return;

    try {
      const response = await fetch(`${API}/organization/invitations/${encodeURIComponent(email)}`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Invitation cancelled");
        fetchTeamData();
      } else {
        toast.error("Failed to cancel invitation");
      }
    } catch (error) {
      toast.error("Failed to cancel invitation");
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from the team? Their leads and tasks will be preserved (unassigned).`)) return;

    try {
      const response = await fetch(`${API}/organization/members/${memberId}/delete`, {
        method: "DELETE",
        credentials: "include"
      });

      if (response.ok) {
        toast.success("Member removed. Data preserved.");
        fetchTeamData();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to remove member");
      }
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  const handleDeactivateMember = async (memberId, memberName) => {
    if (!window.confirm(`Deactivate ${memberName}? They won't be able to log in but their data is preserved.`)) return;
    try {
      const res = await fetch(`${API}/organization/members/${memberId}/deactivate`, {
        method: "POST", credentials: "include"
      });
      if (res.ok) {
        toast.success("Member deactivated");
        fetchTeamData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to deactivate");
      }
    } catch (e) { toast.error("Failed to deactivate"); }
  };

  const handleActivateMember = async (memberId, memberName) => {
    try {
      const res = await fetch(`${API}/organization/members/${memberId}/activate`, {
        method: "POST", credentials: "include"
      });
      if (res.ok) {
        toast.success(`${memberName} reactivated`);
        fetchTeamData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to activate");
      }
    } catch (e) { toast.error("Failed to activate"); }
  };

  const handleChangeRole = async (memberId, memberName, newRole) => {
    try {
      const res = await fetch(`${API}/users/${memberId}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });
      if (res.ok) {
        toast.success(`${memberName} is now ${newRole.replace("_", " ")}`);
        fetchTeamData();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to update role");
      }
    } catch (e) {
      toast.error("Failed to update role");
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const canManageTeam = user?.role === "owner" || user?.role === "admin";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            Team Management
          </h1>
          <p className="text-slate-500 mt-1">
            {org?.name} • {members.length} member{members.length !== 1 ? "s" : ""}
          </p>
        </div>
        
        {canManageTeam && (
          <Button 
            onClick={() => setInviteDialogOpen(true)}
            data-testid="invite-member-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Member
          </Button>
        )}
      </div>

      {/* Seat Usage */}
      {org && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-slate-500" />
                <div>
                  <p className="font-medium">Team Seats</p>
                  <p className="text-sm text-slate-500">
                    {org.seats_used || 1} of {org.seats_purchased || 1} seats used
                  </p>
                </div>
              </div>
              <Badge variant={org.seats_used >= org.seats_purchased ? "destructive" : "outline"}>
                {org.plan?.toUpperCase() || "FREE"} Plan
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Members */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team Members
          </CardTitle>
          <CardDescription>Active members in your organization</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
                {canManageTeam && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => {
                const Icon = roleIcons[member.role] || UserCircle;
                const isCurrentUser = member.user_id === user?.user_id;
                const isOwner = member.role === "owner";
                
                return (
                  <TableRow key={member.user_id} data-testid={`member-row-${member.user_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={member.picture} />
                          <AvatarFallback>{getInitials(member.name)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">
                            {member.name}
                            {isCurrentUser && (
                              <span className="text-xs text-slate-500 ml-2">(you)</span>
                            )}
                            {member.membership_status === "deactivated" && (
                              <Badge className="ml-2 bg-red-100 text-red-600 border-red-200 text-[10px] px-1.5" data-testid={`deactivated-badge-${member.user_id}`}>
                                Deactivated
                              </Badge>
                            )}
                          </p>
                          <p className="text-sm text-slate-500">{member.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={roleColors[member.role]}>
                        <Icon className="w-3 h-3 mr-1" />
                        {member.role?.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500">
                        {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : "—"}
                      </span>
                    </TableCell>
                    {canManageTeam && (
                      <TableCell className="text-right">
                        {!isCurrentUser && !isOwner && (
                          <div className="flex items-center justify-end gap-2">
                            <Select
                              value={member.role}
                              onValueChange={(val) => {
                                if (val !== member.role) handleChangeRole(member.user_id, member.name, val);
                              }}
                              disabled={member.membership_status === "deactivated"}
                            >
                              <SelectTrigger
                                className="h-8 w-[140px] text-xs"
                                data-testid={`change-role-trigger-${member.user_id}`}
                              >
                                <SelectValue placeholder="Change role" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin" data-testid={`role-option-admin-${member.user_id}`}>Admin</SelectItem>
                                <SelectItem value="sales_manager" data-testid={`role-option-manager-${member.user_id}`}>Sales Manager</SelectItem>
                                <SelectItem value="sales_rep" data-testid={`role-option-rep-${member.user_id}`}>Sales Rep</SelectItem>
                              </SelectContent>
                            </Select>
                            {member.membership_status === "deactivated" ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleActivateMember(member.user_id, member.name)}
                                className="text-emerald-600 hover:text-emerald-700 h-8 text-xs"
                                data-testid={`activate-member-${member.user_id}`}
                              >
                                Activate
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeactivateMember(member.user_id, member.name)}
                                className="text-amber-600 hover:text-amber-700 h-8 text-xs"
                                data-testid={`deactivate-member-${member.user_id}`}
                              >
                                Deactivate
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveMember(member.user_id, member.name)}
                              className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                              data-testid={`remove-member-${member.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {canManageTeam && invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Pending Invitations
            </CardTitle>
            <CardDescription>
              Invitations waiting for users to register
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Invited</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.map((invite) => (
                  <TableRow key={invite.email} data-testid={`invite-row-${invite.email}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span>{invite.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {invite.role?.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500">
                        {new Date(invite.created_at).toLocaleDateString()}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelInvitation(invite.email)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`cancel-invite-${invite.email}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your organization. They'll receive access once they register.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="pl-10"
                  data-testid="invite-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-role">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="sales_manager">Sales Manager</SelectItem>
                  <SelectItem value="sales_rep">Sales Rep</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {inviteRole === "admin" && "Full access to settings, team, and billing"}
                {inviteRole === "sales_manager" && "Can manage team members and view reports"}
                {inviteRole === "sales_rep" && "Can manage leads and make calls"}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleInvite} 
              disabled={inviting || !inviteEmail.trim()}
              data-testid="send-invite-btn"
            >
              {inviting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
